import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import TruthThermometer from './TruthThermometer';
import SchemaOrgArticle from './SchemaOrgArticle';
import ScoreGuidelines from './ScoreGuidelines';
import { 
  ChevronDown, ChevronUp, Info, Edit, Check, X, Save, 
  AlertCircle, BarChart2, FileText, Users, Globe, ExternalLink, Copy,
  MessageSquare, ShieldAlert, Activity, Clock
} from 'lucide-react';
import { AUTH_CONFIG } from '../config';
import CardContainer from './CardContainer';

const ArticleDetailView = ({ isAdmin: isAdminProp, articleId: propArticleId, inAdminMode = false }) => {
  // Get ID either from props or from URL params
  const { id: urlId } = useParams();
  const id = propArticleId || urlId;
  
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScoreGuidelines, setShowScoreGuidelines] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [inEditMode, setInEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    summary: '',
    article_assessment: '',
    key_claims: [],
    democratic_pillars: [],
    primary_fallacies: [],
    authoritarian_tactics: []
  });
  const [editSuccess, setEditSuccess] = useState(null);
  const [isAdmin, setIsAdmin] = useState(isAdminProp || false);
  const [activeTab, setActiveTab] = useState('overview');
  const [copySuccess, setCopySuccess] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showFullSummary, setShowFullSummary] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0); // For tab indicator dots
  const tabsContainerRef = useRef(null);
  const leftIndicatorRef = useRef(null);
  const rightIndicatorRef = useRef(null);
  
  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Setup scroll indicators for tabs
  useEffect(() => {
    const tabsContainer = tabsContainerRef.current;
    const leftIndicator = leftIndicatorRef.current;
    const rightIndicator = rightIndicatorRef.current;
    
    if (!tabsContainer || !leftIndicator || !rightIndicator) return;
    
    // Function to check overflow and update indicators
    const updateScrollIndicators = () => {
      const hasOverflow = tabsContainer.scrollWidth > tabsContainer.clientWidth;
      const scrolledToStart = tabsContainer.scrollLeft <= 0;
      const scrolledToEnd = tabsContainer.scrollLeft + tabsContainer.clientWidth >= tabsContainer.scrollWidth - 10;
      
      // Update indicator visibility
      leftIndicator.style.opacity = (hasOverflow && !scrolledToStart) ? '1' : '0';
      rightIndicator.style.opacity = (hasOverflow && !scrolledToEnd) ? '1' : '0';
      
      // Add a subtle pulse animation to the right indicator when first loaded
      // This helps users discover that the tabs are scrollable
      if (hasOverflow && scrolledToStart && !tabsContainer.dataset.hintShown) {
        tabsContainer.dataset.hintShown = 'true';
        rightIndicator.classList.add('animate-pulse');
        setTimeout(() => {
          rightIndicator.classList.remove('animate-pulse');
        }, 2000);
      }
    };
    
    // Run once on mount
    updateScrollIndicators();
    
    // Setup listeners
    tabsContainer.addEventListener('scroll', updateScrollIndicators);
    window.addEventListener('resize', updateScrollIndicators);
    
    // Add a subtle scroll animation on mobile to hint that tabs are scrollable
    if (isMobile) {
      setTimeout(() => {
        if (tabsContainer.scrollWidth > tabsContainer.clientWidth) {
          // Scroll a little bit to show there's more content, then scroll back
          tabsContainer.scrollBy({ left: 40, behavior: 'smooth' });
          setTimeout(() => {
            tabsContainer.scrollBy({ left: -40, behavior: 'smooth' });
          }, 800);
        }
      }, 1500);
    }
    
    // Cleanup
    return () => {
      tabsContainer.removeEventListener('scroll', updateScrollIndicators);
      window.removeEventListener('resize', updateScrollIndicators);
    };
  }, [isMobile]);

  // Check if user is authenticated as admin (if not already set via props)
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (isAdminProp) {
        setIsAdmin(true);
        return;
      }
      
      const authenticated = await AUTH_CONFIG.isAuthenticated();
      setIsAdmin(authenticated);
    };
    
    checkAdminStatus();
  }, [isAdminProp]);
  
  useEffect(() => {
    // Reset expanded state whenever article ID changes
    setShowFullSummary(false);
    
    const fetchArticleDetails = async () => {
      try {
        setLoading(true);
        console.log('Fetching article details for ID:', id);
        
        // First try to get article details
        const response = await fetch(`/api/articles/${id}`);
        console.log('Article response status:', response.status);
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.details || 'Failed to fetch article details');
        }
        
        const data = await response.json();
        console.log('Article data received:', data);
        
        // Now also try to get article highlights (claims)
        try {
          const highlightsResponse = await fetch(`/api/articles/${id}/highlights`);
          console.log('Highlights response status:', highlightsResponse.status);
          
          if (highlightsResponse.ok) {
            const highlightsData = await highlightsResponse.json();
            console.log('Highlights data received:', highlightsData);
            
            // Merge highlights data with article data
            if (highlightsData.claims && highlightsData.claims.length > 0) {
              data.claims = highlightsData.claims;
            }
            
            // Add highlighted content if available
            if (highlightsData.highlighted_content) {
              if (!data.content) data.content = {};
              data.content.html = highlightsData.highlighted_content;
            }
          }
        } catch (highlightsError) {
          console.error('Error fetching highlights:', highlightsError);
          // Don't fail the whole request if highlights fail
        }
        
        // Handle nested article data structure coming from API if present
        // This handles both the old flat structure and the new nested structure
        let processedData = data;
        if (data.article) {
          console.log('Detected nested article data structure, restructuring');
          // Merge top-level properties with article properties, prioritizing article properties
          processedData = {
            ...data,
            ...data.article,
            // Keep the nested publishers, authors, claims arrays as they are
            publishers: data.publishers || [],
            authors: data.authors || [],
            claims: data.claims || [],
            rhetoric_markers: data.rhetoric_markers || [],
            democratic_pillars: data.democratic_pillars || [],
            authoritarian_tactics: data.authoritarian_tactics || [],
            content: data.content || data.article.content
          };
        }
        
        console.log('Final processed article data:', processedData);
        setArticle(processedData);
        
        // Initialize edit form data with all editable fields
        const claimsArray = processedData.claims || [];
        const democraticPillarsArray = processedData.democratic_pillars || [];
        const rhetoricalTacticsArray = processedData.rhetoric_markers || [];
        const authoritarianTacticsArray = processedData.authoritarian_tactics || [];
        
        console.log('Data for edit form', {
          claims: claimsArray,
          democraticPillars: democraticPillarsArray,
          rhetoricalTactics: rhetoricalTacticsArray,
          authoritarianTactics: authoritarianTacticsArray
        });
        
        setEditFormData({
          // General article assessment
          summary: processedData.summary || '',
          article_assessment: processedData.summary || '',
          
          // Key claims
          key_claims: claimsArray.map(claim => ({
            id: claim.id,
            claim_text: claim.claim_text || '',
            rebuttal_text: claim.rebuttal_text || '',
            evidence_links: claim.evidence_links || []
          })),
          
          // Use the detailed relationship data for democratic pillars instead of string array
          democratic_pillars: democraticPillarsArray.length > 0 
            ? democraticPillarsArray.map(pillar => pillar.name || pillar)
            : (Array.isArray(processedData.democratic_pillars_threatened) 
              ? processedData.democratic_pillars_threatened 
              : (typeof processedData.democratic_pillars_threatened === 'string' 
                ? JSON.parse(processedData.democratic_pillars_threatened || '[]') 
                : [])),
          
          // Store original democratic pillars for future reference
          democratic_pillars_source: democraticPillarsArray,
          
          // Use detailed relationship data for rhetorical tactics
          primary_fallacies: rhetoricalTacticsArray.length > 0
            ? rhetoricalTacticsArray.map(tactic => tactic.rhetoric_type || tactic)
            : (Array.isArray(processedData.primary_fallacies) 
              ? processedData.primary_fallacies 
              : (typeof processedData.primary_fallacies === 'string' 
                ? JSON.parse(processedData.primary_fallacies || '[]') 
                : [])),
          
          // Store original rhetoric markers for future reference
          primary_fallacies_source: rhetoricalTacticsArray,
          
          // Use detailed relationship data for authoritarian tactics
          authoritarian_tactics: authoritarianTacticsArray.length > 0
            ? authoritarianTacticsArray.map(tactic => tactic.name || tactic)
            : (Array.isArray(processedData.authoritarian_tactics_employed) 
              ? processedData.authoritarian_tactics_employed 
              : (typeof processedData.authoritarian_tactics_employed === 'string' 
                ? JSON.parse(processedData.authoritarian_tactics_employed || '[]') 
                : [])),
                
          // Store original authoritarian tactics for future reference
          authoritarian_tactics_source: authoritarianTacticsArray
        });
      } catch (err) {
        console.error('Error fetching article details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticleDetails();
  }, [id]);
  
  const handleAnalyzeArticle = async () => {
    if (!article || article.status === 'Analyzed') return;
    
    try {
      setLoading(true);
      
      const response = await fetch('/api/articles/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ article_id: id })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Failed to analyze article');
      }
      
      // Reload article details
      const articleResponse = await fetch(`/api/articles/${id}`);
      const articleData = await articleResponse.json();
      setArticle(articleData);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Determine rating color class based on overall_rating
  const getRatingColorClass = (rating) => {
    if (rating === null || rating === undefined) return 'bg-gray-300';
    if (rating <= 30) return 'bg-red-500';
    if (rating <= 60) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  // Get rating text based on score
  const getRatingText = (score) => {
    if (score === null || score === undefined) return 'Not Rated';
    if (score <= 30) return 'Severely Misleading';
    if (score <= 60) return 'Somewhat Misleading';
    return 'Generally Accurate';
  };
  
  // Handle copy article link to clipboard
  const handleCopyLink = () => {
    const articleTitle = article.title ? article.title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').substring(0, 50).replace(/-+$/, '') : '';
    const url = `${window.location.origin}/a/${id}/${articleTitle}.html`;
    navigator.clipboard.writeText(url)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => console.error('Failed to copy link: ', err));
  };
  
  // Handle editing field input change
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle updating individual claim fields in edit mode
  const handleClaimFieldChange = (claimIndex, field, value) => {
    const updatedClaims = [...editFormData.key_claims];
    updatedClaims[claimIndex] = {
      ...updatedClaims[claimIndex],
      [field]: value
    };
    
    setEditFormData({
      ...editFormData,
      key_claims: updatedClaims
    });
  };
  
  // Handle updating individual rhetoric tactic fields in edit mode
  const handleTacticFieldChange = (tacticIndex, field, value) => {
    const updatedTactics = [...editFormData.primary_fallacies_source];
    updatedTactics[tacticIndex] = {
      ...updatedTactics[tacticIndex],
      [field]: value
    };
    
    // Update the primary_fallacies array as well to keep them in sync
    const updatedNames = updatedTactics.map(tactic => tactic.rhetoric_type || '');
    
    setEditFormData({
      ...editFormData,
      primary_fallacies: updatedNames,
      primary_fallacies_source: updatedTactics
    });
  };
  
  // Handle updating individual democratic pillar fields in edit mode
  const handlePillarFieldChange = (pillarIndex, field, value) => {
    const updatedPillars = [...editFormData.democratic_pillars_source];
    updatedPillars[pillarIndex] = {
      ...updatedPillars[pillarIndex],
      [field]: value
    };
    
    // Update the democratic_pillars array as well to keep them in sync
    const updatedNames = updatedPillars.map(pillar => pillar.name || '');
    
    setEditFormData({
      ...editFormData,
      democratic_pillars: updatedNames,
      democratic_pillars_source: updatedPillars
    });
  };
  
  // Handle updating individual authoritarian tactic fields in edit mode
  const handleAuthTacticFieldChange = (tacticIndex, field, value) => {
    const updatedTactics = [...editFormData.authoritarian_tactics_source];
    updatedTactics[tacticIndex] = {
      ...updatedTactics[tacticIndex],
      [field]: value
    };
    
    // Update the authoritarian_tactics array as well to keep them in sync
    const updatedNames = updatedTactics.map(tactic => tactic.name || '');
    
    setEditFormData({
      ...editFormData,
      authoritarian_tactics: updatedNames,
      authoritarian_tactics_source: updatedTactics
    });
  };
  
  // Legacy function - will be phased out with the move to the new editor
  const handleEditClaim = async (claim, updatedText) => {
    if (!claim.id) return;
    
    try {
      setLoading(true);
      
      const response = await fetch(`/api/articles/${id}/claims/${claim.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...claim,
          claim_text: updatedText?.claim_text || claim.claim_text,
          rebuttal_text: updatedText?.rebuttal_text || claim.rebuttal_text
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update claim');
      }
      
      const result = await response.json();
      
      // Update the claim in the UI
      if (article && article.claims) {
        const updatedClaims = article.claims.map(c => 
          c.id === claim.id ? result.claim : c
        );
        
        setArticle({
          ...article,
          claims: updatedClaims
        });
      }
      
      setEditSuccess('Claim updated successfully');
      setTimeout(() => setEditSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setEditingField(null);
    }
  };
  
  // Handle saving summary edit
  const handleSaveSummary = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`/api/articles/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...article,
          summary: editFormData.summary
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update article summary');
      }
      
      // Update the article in the UI
      setArticle({
        ...article,
        summary: editFormData.summary
      });
      
      setEditingField(null);
      setEditSuccess('Summary updated successfully');
      setTimeout(() => setEditSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle saving all edits in one go
  const handleSaveAllEdits = async () => {
    try {
      setLoading(true);
      
      // Create update data object for article
      const updateData = {
        summary: editFormData.article_assessment,
        democratic_pillars_threatened: editFormData.democratic_pillars,
        primary_fallacies: editFormData.primary_fallacies,
        authoritarian_tactics_employed: editFormData.authoritarian_tactics
      };
      
      // First update the article (summary, pillars, tactics, etc.)
      const articleResponse = await fetch(`/api/articles/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...article,
          ...updateData
        }),
      });
      
      if (!articleResponse.ok) {
        throw new Error('Failed to update article data');
      }
      
      // Then update each claim if needed
      const claimPromises = [];
      for (const claim of editFormData.key_claims) {
        // Only update claims that have an ID (existing claims)
        if (claim.id) {
          const claimPromise = fetch(`/api/articles/${id}/claims/${claim.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(claim),
          });
          claimPromises.push(claimPromise);
        }
      }
      
      // Wait for all claim updates to complete
      await Promise.all(claimPromises);
      
      // Update the article in the UI
      const updatedArticle = {
        ...article,
        ...updateData,
        claims: editFormData.key_claims
      };
      
      setArticle(updatedArticle);
      setInEditMode(false);
      setEditSuccess('Article updated successfully');
      setTimeout(() => setEditSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving article edits:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Legacy function for individual field updates - will be phased out
  const handleSaveAnalysis = async (field) => {
    try {
      setLoading(true);
      
      const updateData = {};
      
      // Update specific field based on which one we're editing
      if (field === 'democratic_pillars') {
        updateData.democratic_pillars_threatened = editFormData.democratic_pillars;
      } else if (field === 'primary_fallacies') {
        updateData.primary_fallacies = editFormData.primary_fallacies;
      } else if (field === 'authoritarian_tactics') {
        updateData.authoritarian_tactics_employed = editFormData.authoritarian_tactics;
      }
      
      const response = await fetch(`/api/articles/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...article,
          ...updateData
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update ${field.replace('_', ' ')}`);
      }
      
      // Update the article in the UI
      setArticle({
        ...article,
        ...updateData
      });
      
      setEditingField(null);
      setEditSuccess(`${field.replace('_', ' ')} updated successfully`);
      setTimeout(() => setEditSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle editing tactics or pillars as comma-separated list
  const handleEditListChange = (e) => {
    const { name, value } = e.target;
    
    // Convert comma-separated string to array
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    
    setEditFormData(prev => ({
      ...prev,
      [name]: items
    }));
  };
  
  // Handle deleting a pillar from the database
  const handleDeletePillar = async (pillarId) => {
    if (!pillarId) return;
    
    try {
      setLoading(true);
      
      const response = await fetch(`/api/articles/${id}/pillars/${pillarId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete pillar');
      }
      
      const result = await response.json();
      
      // Update the democratic_pillars in the article
      if (article) {
        setArticle({
          ...article,
          democratic_pillars_threatened: result.remaining_pillars
        });
      }
      
      setEditSuccess('Pillar deleted successfully');
      setTimeout(() => setEditSuccess(null), 3000);
    } catch (err) {
      console.error("Error deleting pillar:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle deleting a tactic from the database
  const handleDeleteTactic = async (tacticId) => {
    if (!tacticId) return;
    
    try {
      setLoading(true);
      
      const response = await fetch(`/api/articles/${id}/tactics/${tacticId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete tactic');
      }
      
      const result = await response.json();
      
      // Update the tactics in the article
      if (article) {
        setArticle({
          ...article,
          primary_fallacies: result.remaining_tactics
        });
      }
      
      setEditSuccess('Tactic deleted successfully');
      setTimeout(() => setEditSuccess(null), 3000);
    } catch (err) {
      console.error("Error deleting tactic:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Get color based on score for modern UI
  const getScoreColor = (score) => {
    if (score === null || score === undefined) return { bg: 'bg-gray-200', text: 'text-gray-600', border: 'border-gray-300' };
    if (score <= 30) return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' };
    if (score <= 60) return { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' };
    return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' };
  };

  // Loading state
  if (loading && !article) {
    return (
      <div className="w-full py-8">
        <div className="w-full">
          <CardContainer>
            <div className="p-8 flex flex-col items-center justify-center">
              <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600 text-lg">Loading article analysis...</p>
            </div>
          </CardContainer>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="w-full py-8">
        <div className="w-full">
          <CardContainer>
            <div className="p-6">
              <div className="flex items-start">
                <AlertCircle className="text-red-500 mr-4 mt-1" size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-red-700 mb-2">Error loading article</h3>
                  <p className="text-gray-700 mb-4">{error}</p>
                  <Link to="/articles" className="inline-flex items-center text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                    ← Back to Articles
                  </Link>
                </div>
              </div>
            </div>
          </CardContainer>
        </div>
      </div>
    );
  }
  
  // Not found state
  if (!article) {
    return (
      <div className="w-full py-8">
        <div className="w-full">
          <CardContainer>
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-4">Article not found</p>
              <Link to="/articles" className="inline-flex items-center text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                ← Back to Articles
              </Link>
            </div>
          </CardContainer>
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full py-8">
      {/* SEO and Schema.org markup */}
      <Helmet>
        <title>{article.title ? `Analysis: ${article.title}` : 'Article Analysis'}</title>
        <meta name="description" content={article.summary || `Analysis of ${article.title}`} />
        
        {/* Canonical URL - formatted to ensure consistency */}
        <link rel="canonical" href={`${window.location.origin}/a/${article.id}/${
          article.title 
            ? article.title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').substring(0, 50).replace(/-+$/, '') 
            : 'article'
        }.html`} />
        
        {/* Open Graph meta tags */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title ? `Analysis: ${article.title}` : 'Article Analysis'} />
        <meta property="og:description" content={article.summary || `Analysis of ${article.title}`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content={`${window.location.origin}/images/social-share.jpg`} />
        <meta property="og:site_name" content="Democracy Analysis Project" />
        
        {/* Twitter card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title ? `Analysis: ${article.title}` : 'Article Analysis'} />
        <meta name="twitter:description" content={article.summary || `Analysis of ${article.title}`} />
        <meta name="twitter:image" content={`${window.location.origin}/images/social-share.jpg`} />
        
        {/* Article-specific meta tags */}
        <meta property="article:published_time" content={article.publication_date ? new Date(article.publication_date).toISOString() : ''} />
        <meta property="article:modified_time" content={article.analysis_date ? new Date(article.analysis_date).toISOString() : ''} />
        <meta property="article:author" content={article.authors && article.authors.length > 0 ? article.authors[0].name : ''} />
      </Helmet>
      
      {/* Schema.org structured data */}
      <SchemaOrgArticle article={article} />
      
      {/* Back navigation - hide when in admin mode */}
      {!inAdminMode && (
        <div className="mb-1">
          <Link to="/a" className="text-blue-600 hover:text-blue-800 inline-flex items-center hover:-translate-x-0.5 transition-transform duration-150">
            ← Back to Articles
          </Link>
        </div>
      )}
      
      {/* Admin edit mode controls - always visible at the top when admin */}
      {isAdmin && (
        <div className="bg-white shadow-md rounded-lg p-4 mb-4 border w-full">
          <div className="flex justify-between items-center">
            <div className="text-gray-700 font-medium">
              {inEditMode ? 'Editing Article...' : 'Admin Controls'}
            </div>
            <div className="flex gap-2">
              {inEditMode ? (
                <>
                  <button 
                    onClick={() => {
                      // Reset form data to original article data
                      const claimsArray = article.claims || [];
                      const democraticPillarsArray = article.democratic_pillars || [];
                      const rhetoricalTacticsArray = article.rhetoric_markers || [];
                      const authoritarianTacticsArray = article.authoritarian_tactics || [];
                      
                      setEditFormData({
                        summary: article.summary || '',
                        article_assessment: article.summary || '',
                        key_claims: claimsArray.map(claim => ({
                          id: claim.id,
                          claim_text: claim.claim_text || '',
                          rebuttal_text: claim.rebuttal_text || '',
                          evidence_links: claim.evidence_links || []
                        })),
                        // Use detailed relationship data for democratic pillars
                        democratic_pillars: democraticPillarsArray.length > 0 
                          ? democraticPillarsArray.map(pillar => pillar.name || pillar)
                          : (Array.isArray(article.democratic_pillars_threatened) 
                            ? article.democratic_pillars_threatened 
                            : (typeof article.democratic_pillars_threatened === 'string' 
                              ? JSON.parse(article.democratic_pillars_threatened || '[]') 
                              : [])),
                        
                        // Store original pillars for reference
                        democratic_pillars_source: democraticPillarsArray,
                        
                        // Use detailed relationship data for rhetorical tactics
                        primary_fallacies: rhetoricalTacticsArray.length > 0
                          ? rhetoricalTacticsArray.map(tactic => tactic.rhetoric_type || tactic)
                          : (Array.isArray(article.primary_fallacies) 
                            ? article.primary_fallacies 
                            : (typeof article.primary_fallacies === 'string' 
                              ? JSON.parse(article.primary_fallacies || '[]') 
                              : [])),
                        
                        // Store original tactics for reference
                        primary_fallacies_source: rhetoricalTacticsArray,
                        
                        // Use detailed relationship data for authoritarian tactics
                        authoritarian_tactics: authoritarianTacticsArray.length > 0
                          ? authoritarianTacticsArray.map(tactic => tactic.name || tactic)
                          : (Array.isArray(article.authoritarian_tactics_employed) 
                            ? article.authoritarian_tactics_employed 
                            : (typeof article.authoritarian_tactics_employed === 'string' 
                              ? JSON.parse(article.authoritarian_tactics_employed || '[]') 
                              : [])),
                              
                        // Store original tactics for reference  
                        authoritarian_tactics_source: authoritarianTacticsArray
                      });
                      
                      // Exit edit mode
                      setInEditMode(false);
                    }}
                    className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                  >
                    <X className="w-4 h-4 mr-1.5" /> Cancel
                  </button>
                  <button 
                    onClick={handleSaveAllEdits}
                    className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 flex items-center transition-colors"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-1.5" /> Save Changes
                      </>
                    )}
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => setInEditMode(true)}
                  className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 flex items-center transition-colors"
                >
                  <Edit className="w-4 h-4 mr-1.5" /> Edit Article
                </button>
              )}
            </div>
          </div>
            
          {/* Success message for edits */}
          {editSuccess && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-center">
              <Check className="inline w-4 h-4 mr-2" /> {editSuccess}
            </div>
          )}
          
          {/* Error message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm flex items-center">
              <X className="inline w-4 h-4 mr-2" /> {error}
            </div>
          )}
        </div>
      )}
      
      {/* Main article card with tab interface */}
      <div className="w-full">
        <CardContainer>
          {/* Article header */}
          <div className="bg-white p-4 border-b border-gray-200">
            <div className="flex flex-col items-start gap-4">
              {/* Article details */}
              <div className="w-full">
                <h1 className="text-xl font-bold mb-2 text-gray-900">{article.title}</h1>
                {/* Mobile view - compact publisher/author/date */}
                <div className="block md:hidden text-sm text-gray-600 mb-3">
                  {article.publishers && article.publishers.length > 0 && (
                    <div className="mb-1.5 flex items-center">
                      <Users className="w-3.5 h-3.5 mr-1 opacity-70" />
                      <span className="font-medium text-xs">
                        {article.publishers[0].url ? (
                          <a href={article.publishers[0].url} target="_blank" rel="noopener noreferrer nofollow" className="text-blue-600 hover:text-blue-800 underline">
                            {article.publishers[0].name}
                          </a>
                        ) : (article.publishers[0].name || article.publisher_name || article.publisher || 'Unknown')}
                      </span>
                    </div>
                  )}
                  {article.authors && article.authors.length > 0 && (
                    <div className="mb-1.5 flex items-center">
                      <Users className="w-3.5 h-3.5 mr-1 opacity-70" />
                      <span className="font-medium text-xs">
                        {article.authors[0].url ? (
                          <a href={article.authors[0].url} target="_blank" rel="noopener noreferrer nofollow" className="text-blue-600 hover:text-blue-800 underline">
                            {article.authors[0].name}
                          </a>
                        ) : (article.authors[0].name || article.author_name || article.author || 'Unknown')}
                      </span>
                    </div>
                  )}
                  {article.publication_date && (
                    <div className="flex items-center">
                      <Clock className="w-3.5 h-3.5 mr-1 opacity-70" />
                      <span className="font-medium text-xs">
                        {new Date(article.publication_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Desktop view - original layout */}
                <div className="hidden md:flex md:flex-wrap text-sm text-gray-600 mb-3">
                  <div className="mr-4 mb-2">
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-1.5 opacity-70" />
                      <span className="font-medium">Publisher:</span>
                      <span className="ml-1.5">
                        {article.publishers && article.publishers.length > 0 && article.publishers[0].url ? (
                          <a href={article.publishers[0].url} target="_blank" rel="noopener noreferrer nofollow" className="text-blue-600 hover:text-blue-800 underline">
                            {article.publishers[0].name}
                          </a>
                        ) : (article.publishers && article.publishers.length > 0 ? article.publishers[0].name : (article.publisher_name || article.publisher || 'Unknown'))}
                      </span>
                    </div>
                  </div>
                  <div className="mr-4 mb-2">
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-1.5 opacity-70" />
                      <span className="font-medium">Author:</span>
                      <span className="ml-1.5">
                        {article.authors && article.authors.length > 0 && article.authors[0].url ? (
                          <a href={article.authors[0].url} target="_blank" rel="noopener noreferrer nofollow" className="text-blue-600 hover:text-blue-800 underline">
                            {article.authors[0].name}
                          </a>
                        ) : (article.authors && article.authors.length > 0 ? article.authors[0].name : (article.author_name || article.author || 'Unknown'))}
                      </span>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1.5 opacity-70" />
                      <span className="font-medium">Date:</span>
                      <span className="ml-1.5">
                        {article.publication_date ? new Date(article.publication_date).toLocaleDateString() : 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-2">
                  {/* Article URL if available */}
                  {article.url && (
                    <a 
                      href={article.url} 
                      target="_blank" 
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center text-xs px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      View Original Article
                    </a>
                  )}
                  
                  {/* Admin status controls */}
                  {isAdmin && !inEditMode && (
                    <div className="flex items-center space-x-2">
                      {article.status !== "Published" && (
                        <button 
                          onClick={async () => {
                            try {
                              const response = await fetch(`/api/articles/${id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  ...article,
                                  status: 'Published'
                                }),
                              });
                              
                              if (!response.ok) {
                                throw new Error('Failed to update article status');
                              }
                              
                              setArticle({
                                ...article,
                                status: 'Published'
                              });
                              
                              setEditSuccess('Article published successfully');
                              setTimeout(() => setEditSuccess(null), 3000);
                            } catch (err) {
                              setError(err.message);
                            }
                          }}
                          className="px-2 py-1 text-xs font-medium rounded-md text-white bg-green-500 hover:bg-green-600 transition-colors"
                        >
                          Publish
                        </button>
                      )}
                      {article.status === "Published" && (
                        <button 
                          onClick={async () => {
                            try {
                              const response = await fetch(`/api/articles/${id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  ...article,
                                  status: 'Pending Review'
                                }),
                              });
                              
                              if (!response.ok) {
                                throw new Error('Failed to update article status');
                              }
                              
                              setArticle({
                                ...article,
                                status: 'Pending Review'
                              });
                              
                              setEditSuccess('Article unpublished successfully');
                              setTimeout(() => setEditSuccess(null), 3000);
                            } catch (err) {
                              setError(err.message);
                            }
                          }}
                          className="px-2 py-1 text-xs font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 transition-colors"
                        >
                          Unpublish
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Tab navigation with indicator dots */}
          <div className="border-t border-gray-200 bg-gray-50 relative">
            {/* Indicators removed as requested */}
            
            {/* Scrollable tabs container with tab peek effect */}
            <div 
              ref={tabsContainerRef}
              className="flex overflow-x-auto scrollbar-hide relative w-full pl-2 pr-4"
              style={{ scrollBehavior: 'smooth' }}
            >
              <div className="flex w-full pr-4">
                {/* Tab buttons with slightly adjusted spacing to show peek of next tab */}
                <button
                  onClick={() => {
                    setActiveTab('overview');
                    setActiveTabIndex(0);
                    if (isMobile) setShowFullSummary(false);
                  }}
                  className={`px-3 mr-1 py-3.5 text-sm font-medium flex items-center border-b-2 whitespace-nowrap ${
                    activeTab === 'overview' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } transition-colors`}
                >
                  <FileText className="w-4 h-4 mr-1.5" />
                  Overview
                </button>
                <button
                  onClick={() => {
                    setActiveTab('claims');
                    setActiveTabIndex(1);
                  }}
                  className={`px-3 mr-1 py-3.5 text-sm font-medium flex items-center border-b-2 whitespace-nowrap ${
                    activeTab === 'claims' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } transition-colors`}
                >
                  <MessageSquare className="w-4 h-4 mr-1.5" />
                  Claims
                </button>
                <button
                  onClick={() => {
                    setActiveTab('scores');
                    setActiveTabIndex(2);
                  }}
                  className={`px-3 mr-1 py-3.5 text-sm font-medium flex items-center border-b-2 whitespace-nowrap ${
                    activeTab === 'scores' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } transition-colors`}
                >
                  <BarChart2 className="w-4 h-4 mr-1.5" />
                  Scores
                </button>
                <button
                  onClick={() => {
                    setActiveTab('threats');
                    setActiveTabIndex(3);
                  }}
                  className={`px-3 mr-1 py-3.5 text-sm font-medium flex items-center border-b-2 whitespace-nowrap ${
                    activeTab === 'threats' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } transition-colors`}
                >
                  <ShieldAlert className="w-4 h-4 mr-1.5" />
                  Democratic Pillars
                </button>
                <button
                  onClick={() => {
                    setActiveTab('tactics');
                    setActiveTabIndex(4);
                  }}
                  className={`px-3 mr-1 py-3.5 text-sm font-medium flex items-center border-b-2 whitespace-nowrap ${
                    activeTab === 'tactics' 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } transition-colors`}
                >
                  <Activity className="w-4 h-4 mr-1.5" />
                  Rhetorical Tactics
                </button>
              </div>
            </div>
            
            {/* Indicator dots for tabs - mobile only */}
            <div className="flex justify-center py-1 border-t border-gray-200 md:hidden">
              <div className="flex space-x-1">
                <button 
                  onClick={() => {
                    setActiveTab('overview');
                    setActiveTabIndex(0);
                    if (tabsContainerRef.current) {
                      tabsContainerRef.current.scrollTo({ left: 0, behavior: 'smooth' });
                    }
                  }}
                  className={`h-2 w-2 rounded-full transition-colors ${activeTabIndex === 0 ? 'bg-blue-500' : 'bg-gray-300'}`}
                  aria-label="Go to Overview tab"
                ></button>
                <button 
                  onClick={() => {
                    setActiveTab('claims');
                    setActiveTabIndex(1);
                    if (tabsContainerRef.current) {
                      tabsContainerRef.current.scrollTo({ left: 80, behavior: 'smooth' });
                    }
                  }}
                  className={`h-2 w-2 rounded-full transition-colors ${activeTabIndex === 1 ? 'bg-blue-500' : 'bg-gray-300'}`}
                  aria-label="Go to Claims tab"
                ></button>
                <button 
                  onClick={() => {
                    setActiveTab('scores');
                    setActiveTabIndex(2);
                    if (tabsContainerRef.current) {
                      tabsContainerRef.current.scrollTo({ left: 160, behavior: 'smooth' });
                    }
                  }}
                  className={`h-2 w-2 rounded-full transition-colors ${activeTabIndex === 2 ? 'bg-blue-500' : 'bg-gray-300'}`}
                  aria-label="Go to Scores tab"
                ></button>
                <button 
                  onClick={() => {
                    setActiveTab('threats');
                    setActiveTabIndex(3);
                    if (tabsContainerRef.current) {
                      tabsContainerRef.current.scrollTo({ left: 240, behavior: 'smooth' });
                    }
                  }}
                  className={`h-2 w-2 rounded-full transition-colors ${activeTabIndex === 3 ? 'bg-blue-500' : 'bg-gray-300'}`}
                  aria-label="Go to Threats tab"
                ></button>
                <button 
                  onClick={() => {
                    setActiveTab('tactics');
                    setActiveTabIndex(4);
                    if (tabsContainerRef.current) {
                      tabsContainerRef.current.scrollTo({ left: 320, behavior: 'smooth' });
                    }
                  }}
                  className={`h-2 w-2 rounded-full transition-colors ${activeTabIndex === 4 ? 'bg-blue-500' : 'bg-gray-300'}`}
                  aria-label="Go to Rhetorical tab"
                ></button>
              </div>
            </div>
          </div>
          
          {/* Tab content */}
          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Main assessment visualization with modern score display - desktop version */}
                <div className="hidden md:block bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-gray-800">Overall Accuracy Score</h3>
                    <div className={`text-lg font-bold px-3 py-1 rounded-md ${
                      article.overall_rating === null ? 'bg-gray-100 text-gray-500' :
                      article.overall_rating <= 30 ? 'bg-red-500 text-white' : 
                      article.overall_rating <= 60 ? 'bg-yellow-500 text-white' : 
                      'bg-green-500 text-white'
                    }`}>
                      {article.overall_rating !== null ? article.overall_rating : '?'}/100
                    </div>
                  </div>
                  
                  {/* Score text description */}
                  <div className={`text-base font-medium mb-2 ${
                    article.overall_rating === null ? 'text-gray-600' :
                    article.overall_rating <= 30 ? 'text-red-600' : 
                    article.overall_rating <= 60 ? 'text-yellow-600' : 
                    'text-green-600'
                  }`}>
                    {getRatingText(article.overall_rating)}
                  </div>
                  
                  {/* Modern gradient progress bar */}
                  <div className="w-full h-4 bg-gray-200 rounded-full mt-2 mb-3 overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-700 ease-out ${
                        article.overall_rating <= 30 ? 'bg-red-500' : 
                        article.overall_rating <= 60 ? 'bg-yellow-500' : 
                        'bg-green-500'
                      }`}
                      style={{ width: `${article.overall_rating || 0}%` }}
                    ></div>
                  </div>
                  
                  {/* Detailed explanation */}
                  <div className="text-base text-gray-600 mt-2">
                    This score indicates how factually accurate and reliable the article content is based on our assessment criteria.
                    {article.overall_rating <= 30 && <span className="text-red-600 font-medium"> Content is highly misleading.</span>}
                    {article.overall_rating > 30 && article.overall_rating <= 60 && <span className="text-yellow-600 font-medium"> Content contains misleading information.</span>}
                    {article.overall_rating > 60 && <span className="text-green-600 font-medium"> Content is accurate and reliable.</span>}
                  </div>
                  
                  {/* Score scale */}
                  <div className="flex justify-between text-sm text-gray-500 mt-3">
                    <span>0 (Misleading)</span>
                    <span>50 (Mixed)</span>
                    <span>100 (Accurate)</span>
                  </div>
                </div>
                
                {/* Mobile version - simplified with smaller score display */}
                <div className="block md:hidden bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Overall Accuracy Score</h3>
                  
                  {/* Score text description */}
                  <div className={`text-base font-medium mb-2 ${
                    article.overall_rating === null ? 'text-gray-600' :
                    article.overall_rating <= 30 ? 'text-red-600' : 
                    article.overall_rating <= 60 ? 'text-yellow-600' : 
                    'text-green-600'
                  }`}>
                    {getRatingText(article.overall_rating)}
                  </div>
                  
                  {/* Modern gradient progress bar with score overlay */}
                  <div className="relative w-full mb-4">
                    <div className="h-5 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-700 ease-out ${
                          article.overall_rating <= 30 ? 'bg-red-500' : 
                          article.overall_rating <= 60 ? 'bg-yellow-500' : 
                          'bg-green-500'
                        }`}
                        style={{ width: `${article.overall_rating || 0}%` }}
                      ></div>
                    </div>
                    
                    {/* Small score indicator under the bar */}
                    <div className="flex justify-end mt-1">
                      <span className={`text-xs font-bold ${
                        article.overall_rating === null ? 'text-gray-500' :
                        article.overall_rating <= 30 ? 'text-red-600' : 
                        article.overall_rating <= 60 ? 'text-yellow-600' : 
                        'text-green-600'
                      }`}>
                        Score: {article.overall_rating !== null ? article.overall_rating : '?'}/100
                      </span>
                    </div>
                  </div>
                  
                  {/* Detailed explanation */}
                  <div className="text-sm text-gray-600">
                    This score indicates how factually accurate and reliable the article content is based on our assessment criteria.
                    {article.overall_rating <= 30 && <span className="text-red-600 font-medium"> Content is highly misleading.</span>}
                    {article.overall_rating > 30 && article.overall_rating <= 60 && <span className="text-yellow-600 font-medium"> Content contains misleading information.</span>}
                    {article.overall_rating > 60 && <span className="text-green-600 font-medium"> Content is accurate and reliable.</span>}
                  </div>
                  
                  {/* Score scale */}
                  <div className="flex justify-between text-sm text-gray-500 mt-3">
                    <span>0 (Misleading)</span>
                    <span>50 (Mixed)</span>
                    <span>100 (Accurate)</span>
                  </div>
                </div>
                
                {/* Summary section */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Analysis Summary</h3>
                  {inEditMode ? (
                    <div className="mb-4">
                      <textarea
                        name="article_assessment"
                        value={editFormData.article_assessment}
                        onChange={handleEditChange}
                        rows={6}
                        className="w-full p-3 border border-gray-300 rounded-md"
                        placeholder="Enter article assessment"
                      ></textarea>
                    </div>
                  ) : (
                    <div className="prose max-w-none">
                      {article.summary ? (
                        <>
                          {/* Mobile view with 10-line limit and show more/less toggle */}
                          <div className="block md:hidden text-gray-700">
                            {/* Using max-height with overflow hidden instead of line-clamp for better control */}
                            <div 
                              className="overflow-hidden transition-all duration-300"
                              style={{ maxHeight: showFullSummary ? '9999px' : '240px' }}
                            >
                              {article.summary.split('\n')
                                .filter(p => p.trim())
                                .map((paragraph, index) => (
                                  <p key={index} className="mb-3">{paragraph}</p>
                                ))
                              }
                            </div>
                            
                            {/* Add gradient fade effect when collapsed */}
                            {!showFullSummary && article.summary.length > 500 && (
                              <div className="relative -mt-12 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
                            )}
                            
                            {/* Show More/Less button if summary is longer than ~10 lines */}
                            {article.summary.length > 500 && (
                              <button 
                                onClick={() => setShowFullSummary(!showFullSummary)}
                                className="flex items-center text-blue-600 hover:text-blue-800 text-sm mt-2 mb-4"
                              >
                                {showFullSummary ? (
                                  <>
                                    <ChevronUp className="w-4 h-4 mr-1" /> Show Less
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="w-4 h-4 mr-1" /> Show More
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                          
                          {/* Desktop view - original layout */}
                          <div className="hidden md:block text-gray-700">
                            {article.summary.split('\n').map((paragraph, index) => (
                              paragraph.trim() ? <p key={index} className="mb-3">{paragraph}</p> : <br key={index} />
                            ))}
                          </div>
                        </>
                      ) : (
                        <p className="text-gray-700">No summary available.</p>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Score guidelines collapsible section - desktop only */}
                <div className="mt-6 hidden md:block">
                  <button 
                    onClick={() => setShowScoreGuidelines(!showScoreGuidelines)}
                    className="text-sm flex items-center text-blue-600 hover:text-blue-800"
                  >
                    <Info className="w-4 h-4 mr-1.5" />
                    {showScoreGuidelines ? 'Hide Scoring Guidelines' : 'Show Scoring Guidelines'}
                    {showScoreGuidelines ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                  </button>
                  
                  {showScoreGuidelines && (
                    <div className="mt-3 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                      <ScoreGuidelines />
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Key Claims Tab */}
            {activeTab === 'claims' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">Key Claims & Rebuttals</h3>
                {inEditMode ? (
                  <div className="space-y-4">
                    {editFormData.key_claims && editFormData.key_claims.length > 0 ? (
                      editFormData.key_claims.map((claim, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm relative">
                          {/* Delete claim button */}
                          <div className="absolute top-2 right-2">
                            <button 
                              onClick={async () => {
                                // If the claim has an ID, delete it from the database
                                if (claim.id) {
                                  try {
                                    const response = await fetch(`/api/articles/${id}/claims/${claim.id}`, {
                                      method: 'DELETE',
                                    });
                                    
                                    if (!response.ok) {
                                      throw new Error('Failed to delete claim');
                                    }
                                    
                                    // Show success message
                                    setEditSuccess('Claim deleted successfully');
                                    setTimeout(() => setEditSuccess(null), 3000);
                                  } catch (err) {
                                    // Show error message
                                    setError(`Failed to delete claim: ${err.message}`);
                                    setTimeout(() => setError(null), 5000);
                                    return; // Don't remove from UI if delete failed
                                  }
                                }
                                
                                // Remove this claim from the UI
                                const updatedClaims = [...editFormData.key_claims];
                                updatedClaims.splice(index, 1);
                                
                                setEditFormData({
                                  ...editFormData,
                                  key_claims: updatedClaims
                                });
                              }}
                              className="text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 p-1 rounded"
                              title="Delete this claim"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 6h18"></path>
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                              </svg>
                            </button>
                          </div>
                          
                          <div className="mb-3">
                            <label className="block text-xs font-medium mb-1">Claim Text</label>
                            <textarea
                              value={claim.claim_text || ''}
                              onChange={(e) => handleClaimFieldChange(index, 'claim_text', e.target.value)}
                              className="w-full p-2 text-sm border border-gray-300 rounded"
                              rows="3"
                            ></textarea>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Rebuttal Text</label>
                            <textarea
                              value={claim.rebuttal_text || ''}
                              onChange={(e) => handleClaimFieldChange(index, 'rebuttal_text', e.target.value)}
                              className="w-full p-2 text-sm border border-gray-300 rounded"
                              rows="3"
                            ></textarea>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-600 text-center">
                        No claims found for this article.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {article.claims && article.claims.length > 0 ? (
                      article.claims.map((claim, index) => (
                        <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                          <div className="p-4 bg-red-50 border-l-4 border-red-400">
                            <h3 className="text-base font-medium text-red-800">Claim</h3>
                            <p className="mt-1 text-base text-gray-700">{claim.claim_text}</p>
                          </div>
                          <div className="p-4 bg-green-50 border-l-4 border-green-400">
                            <h3 className="text-base font-medium text-green-800">Reality</h3>
                            <p className="mt-1 text-base text-gray-700">{claim.rebuttal_text}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 text-center">
                        <p className="text-gray-600">No key claims have been identified for this article.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Detailed Scores Tab */}
            {activeTab === 'scores' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">Detailed Score Analysis</h3>
                
                {/* Score cards with detailed analysis */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Truthfulness Card */}
                  <div className="bg-white rounded-lg p-5 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-400 to-blue-600"></div>
                    <div className="flex items-start">
                      <div className="mr-4">
                        <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-baseline">
                          <h3 className="text-base font-medium text-gray-900">Truthfulness</h3>
                          <span className="text-2xl font-bold text-blue-600">
                            {article.truthfulness_score !== null && article.truthfulness_score !== undefined ? 
                              article.truthfulness_score : '?'}
                            <span className="text-sm text-gray-500 font-normal">/100</span>
                          </span>
                        </div>
                        <div className="mt-2 bg-gray-200 rounded-full h-1.5 mb-1">
                          <div 
                            className={`h-1.5 rounded-full ${
                              !article.truthfulness_score ? 'bg-gray-400' :
                              article.truthfulness_score <= 30 ? 'bg-red-500' : 
                              article.truthfulness_score <= 60 ? 'bg-yellow-500' : 
                              'bg-green-500'
                            }`}
                            style={{ width: `${article.truthfulness_score || 0}%` }}
                          ></div>
                        </div>
                        <p className="text-sm font-medium text-blue-600 uppercase tracking-wider mt-2">
                          {article.truthfulness_score !== null && article.truthfulness_score !== undefined ? 
                            getRatingText(article.truthfulness_score) : 'Not Rated'}
                        </p>
                        <p className="text-base text-gray-600 mt-2">
                          Evaluates factual accuracy, source verification, and whether claims are supported by evidence.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Constitutional Accuracy Card */}
                  <div className="bg-white rounded-lg p-5 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-400 to-blue-600"></div>
                    <div className="flex items-start">
                      <div className="mr-4">
                        <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-baseline">
                          <h3 className="text-base font-medium text-gray-900">Constitutional Accuracy</h3>
                          <span className="text-2xl font-bold text-blue-600">
                            {article.constitutional_accuracy_score !== null && article.constitutional_accuracy_score !== undefined ? 
                              article.constitutional_accuracy_score : '?'}
                            <span className="text-sm text-gray-500 font-normal">/100</span>
                          </span>
                        </div>
                        <div className="mt-2 bg-gray-200 rounded-full h-1.5 mb-1">
                          <div 
                            className={`h-1.5 rounded-full ${
                              !article.constitutional_accuracy_score ? 'bg-gray-400' :
                              article.constitutional_accuracy_score <= 30 ? 'bg-red-500' : 
                              article.constitutional_accuracy_score <= 60 ? 'bg-yellow-500' : 
                              'bg-green-500'
                            }`}
                            style={{ width: `${article.constitutional_accuracy_score || 0}%` }}
                          ></div>
                        </div>
                        <p className="text-sm font-medium text-blue-600 uppercase tracking-wider mt-2">
                          {article.constitutional_accuracy_score !== null && article.constitutional_accuracy_score !== undefined ? 
                            getRatingText(article.constitutional_accuracy_score) : 'Not Rated'}
                        </p>
                        <p className="text-base text-gray-600 mt-2">
                          Measures accuracy when discussing constitutional rights, laws, and government systems.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Source Credibility Card */}
                  <div className="bg-white rounded-lg p-5 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-400 to-blue-600"></div>
                    <div className="flex items-start">
                      <div className="mr-4">
                        <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-baseline">
                          <h3 className="text-base font-medium text-gray-900">Source Credibility</h3>
                          <span className="text-2xl font-bold text-blue-600">
                            {article.source_credibility_score !== null && article.source_credibility_score !== undefined ? 
                              article.source_credibility_score : '?'}
                            <span className="text-sm text-gray-500 font-normal">/100</span>
                          </span>
                        </div>
                        <div className="mt-2 bg-gray-200 rounded-full h-1.5 mb-1">
                          <div 
                            className={`h-1.5 rounded-full ${
                              !article.source_credibility_score ? 'bg-gray-400' :
                              article.source_credibility_score <= 30 ? 'bg-red-500' : 
                              article.source_credibility_score <= 60 ? 'bg-yellow-500' : 
                              'bg-green-500'
                            }`}
                            style={{ width: `${article.source_credibility_score || 0}%` }}
                          ></div>
                        </div>
                        <p className="text-sm font-medium text-blue-600 uppercase tracking-wider mt-2">
                          {article.source_credibility_score !== null && article.source_credibility_score !== undefined ? 
                            getRatingText(article.source_credibility_score) : 'Not Rated'}
                        </p>
                        <p className="text-base text-gray-600 mt-2">
                          Evaluates the reliability of sources cited and the author's expertise on the subject.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Rhetoric Assessment Card */}
                  <div className="bg-white rounded-lg p-5 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-400 to-blue-600"></div>
                    <div className="flex items-start">
                      <div className="mr-4">
                        <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-baseline">
                          <h3 className="text-base font-medium text-gray-900">Rhetoric Assessment</h3>
                          <span className="text-2xl font-bold text-blue-600">
                            {article.rhetoric_score !== null && article.rhetoric_score !== undefined ? 
                              article.rhetoric_score : '?'}
                            <span className="text-sm text-gray-500 font-normal">/100</span>
                          </span>
                        </div>
                        <div className="mt-2 bg-gray-200 rounded-full h-1.5 mb-1">
                          <div 
                            className={`h-1.5 rounded-full ${
                              !article.rhetoric_score ? 'bg-gray-400' :
                              article.rhetoric_score <= 30 ? 'bg-red-500' : 
                              article.rhetoric_score <= 60 ? 'bg-yellow-500' : 
                              'bg-green-500'
                            }`}
                            style={{ width: `${article.rhetoric_score || 0}%` }}
                          ></div>
                        </div>
                        <p className="text-sm font-medium text-blue-600 uppercase tracking-wider mt-2">
                          {article.rhetoric_score !== null && article.rhetoric_score !== undefined ? 
                            getRatingText(article.rhetoric_score) : 'Not Rated'}
                        </p>
                        <p className="text-base text-gray-600 mt-2">
                          Assesses the use of rhetoric, logical fallacies, and persuasive language techniques.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Democratic impact scores */}
                {article.democratic_impact_scores && (
                  <div className="mt-6 bg-white rounded-lg p-6 shadow-sm">
                    <h3 className="text-md font-semibold mb-4 text-gray-800">Democratic Impact</h3>
                    <div className="grid grid-cols-1 gap-4">
                      {Object.entries(article.democratic_impact_scores).map(([key, value]) => (
                        <div key={key} className="bg-white p-3 border border-gray-200 rounded-lg">
                          <div className="flex justify-between items-baseline mb-2">
                            <h3 className="font-medium text-gray-800">{key}</h3>
                            <span className="text-lg font-bold text-blue-600">{value}<span className="text-sm text-gray-500 font-normal">/5</span></span>
                          </div>
                          <div className="mt-2 bg-gray-200 rounded-full h-1.5 mb-1">
                            <div 
                              className="bg-blue-600 h-1.5 rounded-full" 
                              style={{ width: `${Math.round((value / 5) * 100)}%` }}
                            ></div>
                          </div>
                          <p className="text-sm font-medium text-blue-600 uppercase tracking-wider mt-2">
                            {value >= 4 ? "Severe Impact" : 
                             value >= 3 ? "Significant Impact" : 
                             value >= 2 ? "Moderate Impact" : "Minor Impact"}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Democratic Threats Tab */}
            {activeTab === 'threats' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">Democratic Pillars Under Threat</h3>
                {/* First check if we have rich pillar data */}
                {article.democratic_pillars && article.democratic_pillars.length > 0 ? (
                  <div>
                    {inEditMode ? (
                      <div className="space-y-4">
                        {editFormData.democratic_pillars_source && editFormData.democratic_pillars_source.length > 0 ? (
                          editFormData.democratic_pillars_source.map((pillar, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm relative">
                              {/* Delete pillar button */}
                              <div className="absolute top-2 right-2">
                                <button 
                                  onClick={() => {
                                    // Remove this pillar from the array
                                    const updatedPillars = [...editFormData.democratic_pillars_source];
                                    updatedPillars.splice(index, 1);
                                    
                                    // Update the pillars array to match
                                    const updatedPillarNames = updatedPillars.map(p => p.name || '');
                                    
                                    setEditFormData({
                                      ...editFormData,
                                      democratic_pillars: updatedPillarNames,
                                      democratic_pillars_source: updatedPillars
                                    });
                                  }}
                                  className="text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 p-1 rounded"
                                  title="Delete this pillar"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 6h18"></path>
                                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                  </svg>
                                </button>
                              </div>
                              
                              <div className="mb-3">
                                <label className="block text-xs font-medium mb-1">Pillar Name</label>
                                <input
                                  type="text"
                                  value={pillar.name || ''}
                                  onChange={(e) => handlePillarFieldChange(index, 'name', e.target.value)}
                                  className="w-full p-2 text-sm border border-gray-300 rounded"
                                />
                              </div>
                              {pillar.description && (
                                <div className="mb-3">
                                  <label className="block text-xs font-medium mb-1">Description</label>
                                  <textarea
                                    value={pillar.description || ''}
                                    onChange={(e) => handlePillarFieldChange(index, 'description', e.target.value)}
                                    className="w-full p-2 text-sm border border-gray-300 rounded"
                                    rows="2"
                                  ></textarea>
                                </div>
                              )}
                              {(pillar.impact_explanation || pillar.justification) && (
                                <div>
                                  <label className="block text-xs font-medium mb-1">Impact Explanation</label>
                                  <textarea
                                    value={pillar.impact_explanation || pillar.justification || ''}
                                    onChange={(e) => handlePillarFieldChange(index, pillar.impact_explanation ? 'impact_explanation' : 'justification', e.target.value)}
                                    className="w-full p-2 text-sm border border-gray-300 rounded"
                                    rows="2"
                                  ></textarea>
                                </div>
                              )}
                            </div>
                          ))
                        ) : Array.isArray(editFormData.democratic_pillars) && editFormData.democratic_pillars.length > 0 ? (
                          <div>
                            <label className="block text-xs font-medium mb-1">Democratic Pillars (comma-separated)</label>
                            <textarea
                              name="democratic_pillars"
                              value={editFormData.democratic_pillars.join(', ')}
                              onChange={handleEditListChange}
                              rows={4}
                              className="w-full p-3 border border-gray-300 rounded-md"
                              placeholder="Enter democratic pillars, comma-separated"
                            ></textarea>
                          </div>
                        ) : (
                          <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-600 text-center">
                            No democratic pillars found for this article.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {article.democratic_pillars.map((pillar, index) => (
                          <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                            <div className="p-3 bg-blue-50 border-l-4 border-blue-400">
                              <h3 className="text-base font-medium text-blue-800">{pillar.name}</h3>
                            </div>
                            <div className="p-4">
                              {pillar.description && (
                                <div className="mb-3">
                                  <h4 className="text-base font-semibold text-gray-700 mb-1">Description:</h4>
                                  <p className="text-base text-gray-700">{pillar.description}</p>
                                </div>
                              )}
                              {/* Check both field names that might contain the impact explanation */}
                              {(pillar.impact_explanation || pillar.justification) && (
                                <div>
                                  <h4 className="text-base font-semibold text-red-700 mb-1">Impact:</h4>
                                  <p className="text-base text-gray-700">{pillar.impact_explanation || pillar.justification}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : Array.isArray(article.democratic_pillars_threatened) && article.democratic_pillars_threatened.length > 0 ? (
                  <div>
                    {inEditMode ? (
                      <div>
                        <label className="block text-xs font-medium mb-1">Democratic Pillars (comma-separated)</label>
                        <textarea
                          name="democratic_pillars"
                          value={editFormData.democratic_pillars.join(', ')}
                          onChange={handleEditListChange}
                          rows={4}
                          className="w-full p-3 border border-gray-300 rounded-md"
                          placeholder="Enter democratic pillars, comma-separated"
                        ></textarea>
                      </div>
                    ) : (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <h3 className="font-medium text-blue-800 mb-2">Democratic Pillars Threatened:</h3>
                        <div className="flex flex-wrap gap-2">
                          {article.democratic_pillars_threatened.map((pillar, index) => (
                            <div key={index} className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                              {pillar}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-center">
                    <p className="text-gray-600">No democratic pillars have been identified as threatened by this article.</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Rhetorical Tactics Tab */}
            {activeTab === 'tactics' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">Rhetorical Tactics Identified</h3>
                {/* First check if we have rhetoric markers */}
                {article.rhetoric_markers && article.rhetoric_markers.length > 0 ? (
                  <div>
                    {inEditMode ? (
                      <div className="space-y-4">
                        {editFormData.primary_fallacies_source && editFormData.primary_fallacies_source.length > 0 ? (
                          editFormData.primary_fallacies_source.map((tactic, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm relative">
                              {/* Delete tactic button */}
                              <div className="absolute top-2 right-2">
                                <button 
                                  onClick={() => {
                                    // Remove this tactic from the array
                                    const updatedTactics = [...editFormData.primary_fallacies_source];
                                    updatedTactics.splice(index, 1);
                                    
                                    // Update the fallacies array to match
                                    const updatedFallacies = updatedTactics.map(t => t.rhetoric_type || '');
                                    
                                    setEditFormData({
                                      ...editFormData,
                                      primary_fallacies: updatedFallacies,
                                      primary_fallacies_source: updatedTactics
                                    });
                                  }}
                                  className="text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 p-1 rounded"
                                  title="Delete this tactic"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 6h18"></path>
                                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                  </svg>
                                </button>
                              </div>
                              
                              <div className="mb-3">
                                <label className="block text-xs font-medium mb-1">Tactic Type</label>
                                <input
                                  type="text"
                                  value={tactic.rhetoric_type || ''}
                                  onChange={(e) => handleTacticFieldChange(index, 'rhetoric_type', e.target.value)}
                                  className="w-full p-2 text-sm border border-gray-300 rounded"
                                />
                              </div>
                              {tactic.explanation && (
                                <div className="mb-3">
                                  <label className="block text-xs font-medium mb-1">Explanation</label>
                                  <textarea
                                    value={tactic.explanation || ''}
                                    onChange={(e) => handleTacticFieldChange(index, 'explanation', e.target.value)}
                                    className="w-full p-2 text-sm border border-gray-300 rounded"
                                    rows="2"
                                  ></textarea>
                                </div>
                              )}
                              {(tactic.democratic_threat || tactic.justification) && (
                                <div className="mb-2">
                                  <label className="block text-xs font-medium mb-1">Democratic Threat</label>
                                  <textarea
                                    value={tactic.democratic_threat || tactic.justification || ''}
                                    onChange={(e) => handleTacticFieldChange(index, tactic.democratic_threat ? 'democratic_threat' : 'justification', e.target.value)}
                                    className="w-full p-2 text-sm border border-gray-300 rounded"
                                    rows="2"
                                  />
                                </div>
                              )}
                              {tactic.example_text && (
                                <div className="mb-2">
                                  <label className="block text-xs font-medium mb-1">Example Text</label>
                                  <textarea
                                    value={tactic.example_text || ''}
                                    onChange={(e) => handleTacticFieldChange(index, 'example_text', e.target.value)}
                                    className="w-full p-2 text-sm border border-gray-300 rounded"
                                    rows="2"
                                  />
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-600 text-center">
                            No rhetorical tactics found for this article.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {article.rhetoric_markers.map((tactic, index) => (
                          <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                            <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400">
                              <h3 className="text-base font-medium text-yellow-800">{tactic.rhetoric_type}</h3>
                            </div>
                            <div className="p-4">
                              {tactic.explanation && (
                                <div className="mb-3">
                                  <h4 className="text-base font-semibold text-gray-700 mb-1">Explanation:</h4>
                                  <p className="text-base text-gray-700">{tactic.explanation}</p>
                                </div>
                              )}
                              {/* Check both field names that might contain the threat explanation */}
                              {(tactic.democratic_threat || tactic.justification) && (
                                <div className="mb-3">
                                  <h4 className="text-base font-semibold text-red-700 mb-1">Threat to democracy:</h4>
                                  <p className="text-base text-gray-700">{tactic.democratic_threat || tactic.justification}</p>
                                </div>
                              )}
                              {tactic.example_text && (
                                <div>
                                  <h4 className="text-base font-semibold text-gray-700 mb-1">Example:</h4>
                                  <div className="p-2 bg-gray-50 rounded border border-gray-200">
                                    <p className="text-base text-gray-700 italic">&ldquo;{tactic.example_text}&rdquo;</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : Array.isArray(article.primary_fallacies) && article.primary_fallacies.length > 0 ? (
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
                    <h3 className="font-medium text-yellow-800 mb-2">Rhetorical Tactics Identified:</h3>
                    <div className="flex flex-wrap gap-2">
                      {article.primary_fallacies.map((tactic, index) => (
                        <div key={index} className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                          {tactic}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-center">
                    <p className="text-gray-600">No rhetorical tactics have been identified for this article.</p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Card footer with copy link button */}
          <div className="p-4 border-t border-gray-200 flex justify-end">
            <button 
              onClick={handleCopyLink}
              className="flex items-center px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              <Copy size={16} className="mr-2" />
              {copySuccess ? 'Link Copied!' : 'Copy Article Link'}
            </button>
          </div>
        </CardContainer>
      </div>
    </div>
  );
};

export default ArticleDetailView;